import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
const heroImg = new URL("../../assets/hero-thali.jpg", import.meta.url).toString();
const logo = new URL("../../assets/mononites-logo.jpeg", import.meta.url).toString();

function CountUp({
  to,
  suffix = "",
  duration = 2200,
}: {
  to: number;
  suffix?: string;
  duration?: number;
}) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(eased * to));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [to, duration]);

  return (
    <div ref={ref} className="font-display text-xl sm:text-2xl text-gold tabular-nums">
      {value.toLocaleString()}{suffix}
    </div>
  );
}

const rotatingWords = ["corporates.","offices.","workplaces.", "celebrations.", "gatherings.", "every shift.", "every occasion."];
const DELETE_SPEED = 60;
const TYPE_SPEED = 80;
const PAUSE_AFTER_TYPE = 2200;
const PAUSE_AFTER_DELETE = 300;

function useTypewriter(words: string[]) {
  const [wordIdx, setWordIdx] = useState(0);
  const [displayText, setDisplayText] = useState(words[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const tick = useCallback(() => {
    const currentWord = words[wordIdx];

    if (isDeleting) {
      if (displayText.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayText((prev) => prev.slice(0, -1));
        }, DELETE_SPEED);
      } else {
        setIsDeleting(false);
        setWordIdx((i) => (i + 1) % words.length);
        timeoutRef.current = setTimeout(() => {}, PAUSE_AFTER_DELETE);
      }
    } else {
      if (displayText.length < currentWord.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        }, TYPE_SPEED);
      } else {
        timeoutRef.current = setTimeout(() => {
          setIsDeleting(true);
        }, PAUSE_AFTER_TYPE);
      }
    }
  }, [words, wordIdx, displayText, isDeleting]);

  useEffect(() => {
    timeoutRef.current = setTimeout(tick, isDeleting ? DELETE_SPEED : TYPE_SPEED);
    return () => clearTimeout(timeoutRef.current);
  }, [tick, isDeleting]);

  return displayText;
}

export function Hero() {
  const displayText = useTypewriter(rotatingWords);

  return (
    <section className="relative h-screen flex items-center pt-20 pb-8 overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src={heroImg}
          alt="South Indian thali"
          className="w-full h-full object-cover opacity-40"
          width={1920}
          height={1080}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--navy-deep)] via-[var(--navy-deep)]/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy-deep)] via-transparent to-[var(--navy-deep)]/40" />
      </div>

      {/* Decorative gold orbs */}
      <div className="absolute -top-40 -right-40 h-[400px] w-[400px] sm:h-[600px] sm:w-[600px] rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-1/3 h-[200px] w-[200px] sm:h-[300px] sm:w-[300px] rounded-full bg-[var(--gold)]/6 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-gold" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-gold">
              Premium Catering Services
            </span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground">
            <span className="block">Authentic flavors,</span>
            <span className="block mt-1">
              <span className="shimmer-gold italic">crafted for&nbsp;</span>
              <span className="shimmer-gold">{displayText}</span>
              <span className="inline-block w-[2px] h-[0.85em] bg-gold align-middle ml-0.5 animate-pulse" />
            </span>
          </h1>

          <p className="mt-4 max-w-xl text-sm sm:text-base text-muted-foreground leading-relaxed font-light">
            Mononites Food Services specializes in workplace catering and
            cafeteria management, delivering authentic South Indian vegetarian cuisine
            to teams that value quality and tradition.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[var(--gold-foreground)] font-medium uppercase tracking-[0.12em] text-xs hover:shadow-gold transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              Get A Quote
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="/brochure.png"
              download="Mononites_brochure.png"
              className="sm:hidden inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[var(--gold)]/40 text-gold text-xs font-medium uppercase tracking-[0.12em] hover:border-gold transition-all"
            >
              Download Brochure
            </a>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-4 sm:gap-8 max-w-xs sm:max-w-lg">
            {[
              { to: 5000, suffix: "+", label: "Meals daily" },
              { to: 100, suffix: "%", label: "Pure vegetarian" },
            ].map((s) => (
              <div key={s.label}>
                <CountUp to={s.to} suffix={s.suffix} />
                <div className="text-[9px] sm:text-xs uppercase tracking-widest text-muted-foreground mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Logo orb — hidden on mobile, shown on lg */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="hidden lg:flex lg:col-span-5 justify-center"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[var(--gold)]/20 blur-3xl" />
            <div className="relative h-[280px] w-[280px] xl:h-[320px] xl:w-[320px] rounded-full overflow-hidden ring-4 ring-[var(--gold)]/40 shadow-elevated hover:ring-[var(--gold)]/70 transition-all duration-500">
              <img src={logo} alt="Mononites logo" className="h-full w-full object-cover" />
            </div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-6 rounded-full border border-dashed border-[var(--gold)]/40 pointer-events-none"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-12 rounded-full border border-dotted border-[var(--gold)]/15 pointer-events-none"
            />
          </div>
        </motion.div>
      </div>

      {/* Scrolling marquee ticker */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-t border-[var(--gold)]/15 bg-[var(--navy-deep)]/70 backdrop-blur-sm py-3">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(4)].map((_, rep) => (
            <span key={rep} className="flex items-center">
              {["All-In-One Multi-Cuisine", "Seal of Trust", "Zero Compromise Hygiene","Home Style Connection"].map((item) => (
                <span key={item} className="flex items-center gap-3 mx-10">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span className="text-[11px] uppercase tracking-[0.3em] text-gold/75 font-medium">{item}</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
